import { Play } from "lucide-react";
import VideoLightbox from "@/components/VideoLightbox";

// Site-level trust ribbon between the hero and the problem section. The ENTIRE band
// is the marquee — full-bleed, no card. The blue award block is a LAYERED surface
// (cobalt -> indigo -> violet base, radial depth glow, a soft curved light streak, a
// halftone that dissolves into the white at the diagonal, and an ambient glow joining
// the two sides) so the background reads as intentionally crafted even with no text.
// Each strip is one viewport wide so only one shows; the duplicate is offscreen for
// the seamless loop. Slow right-to-left, pauses on hover. Award -> PR, video -> video.
const ARTICLE_URL =
  "https://cfoleadership.com/mesh-cashio-meshly-ai-and-virtuspect-named-winners-of-2025-fate-pitch-stage-competition/";

// vivid electric-cobalt -> Meshly indigo -> deep indigo (Meshly-blue centered, no
// violet), layered and saturated for pop — not a flat fill.
const BASE =
  "linear-gradient(118deg, hsl(222 92% 61%) 0%, hsl(236 84% 52%) 50%, hsl(245 78% 44%) 100%)";

function Award() {
  return (
    <a
      href={ARTICLE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="relative flex w-[56%] shrink-0 flex-col items-start justify-center gap-1.5 py-3.5 pl-8 pr-12 transition-opacity hover:opacity-[0.97]"
      style={{
        clipPath: "polygon(0 0, 100% 0, calc(100% - 92px) 100%, 0 100%)",
        background: BASE,
        filter: "drop-shadow(3px 0 16px hsl(238 62% 24% / 0.26))",
      }}
    >
      {/* radial depth glow, top-left */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(125% 135% at 20% -12%, rgba(255,255,255,0.30), transparent 55%)" }}
      />
      {/* soft curved light streak — atmospheric, blurred ellipse on an angle */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-[-8%] top-[-45%] h-[190%] w-[58%] -rotate-[19deg] rounded-[42%] blur-[42px]"
        style={{ background: "radial-gradient(closest-side, rgba(150,180,255,0.40), transparent 72%)" }}
      />
      {/* ambient glow along the diagonal so the two sides feel joined */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-28 blur-2xl"
        style={{ background: "linear-gradient(90deg, transparent, hsl(228 98% 66% / 0.45))" }}
      />
      {/* halftone that dissolves into the white at the transition */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.55) 0.7px, transparent 1.4px)",
          backgroundSize: "7px 7px",
          opacity: 0.5,
          WebkitMaskImage: "linear-gradient(101deg, transparent 60%, #000 82%, transparent 99%)",
          maskImage: "linear-gradient(101deg, transparent 60%, #000 82%, transparent 99%)",
        }}
      />

      <span className="relative inline-flex items-center rounded-full bg-accent px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-foreground">
        Winner
      </span>
      <span className="relative text-sm font-bold leading-tight tracking-tight text-white sm:text-base lg:whitespace-nowrap lg:text-lg">
        Finance &amp; Accounting Technology Expo
      </span>
      <img
        src="/cfolc-logo-white.png"
        alt="CFO Leadership Council"
        className="relative h-[0.9rem] w-auto opacity-90"
      />
    </a>
  );
}

function Video() {
  return (
    <VideoLightbox
      youtubeId="Jcas-r2eITQ"
      showIcon={false}
      triggerClassName="group relative flex w-[44%] shrink-0 cursor-pointer items-center justify-center gap-5 py-3 pl-6 pr-10 text-left"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent shadow-[0_5px_16px_-5px_hsl(85_60%_45%/0.6)] transition-transform group-hover:scale-105">
        <Play className="h-5 w-5 translate-x-[1px] fill-foreground text-foreground" />
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-sm text-muted-foreground">Featured in</span>
        <span className="text-xl font-bold tracking-tight text-foreground lg:text-2xl">
          Times Square
        </span>
      </span>
      {/* Times Square footage thumbnail — fills the white + reinforces the feature */}
      <span className="relative hidden h-[68px] w-[120px] shrink-0 overflow-hidden rounded-lg shadow-[0_6px_18px_-8px_rgba(23,23,23,0.4)] ring-1 ring-black/5 md:block">
        <img
          src="https://i.ytimg.com/vi/Jcas-r2eITQ/hqdefault.jpg"
          alt=""
          aria-hidden
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute inset-0 bg-black/10" aria-hidden />
      </span>
    </VideoLightbox>
  );
}

// One strip = one full viewport wide, so only one shows at a time.
function Strip({ ariaHidden }: { ariaHidden?: boolean }) {
  return (
    <div className="flex min-h-[92px] w-screen shrink-0 items-stretch" aria-hidden={ariaHidden}>
      <Award />
      <Video />
    </div>
  );
}

export default function CredibilityStrip() {
  return (
    <section
      aria-label="Recognition"
      className="overflow-hidden border-y border-border shadow-[0_10px_30px_-22px_rgba(23,23,23,0.25)]"
      style={{
        // very subtle cool-blue tint instead of dead white, layered
        background:
          "linear-gradient(180deg, hsl(238 46% 98.7%), hsl(238 40% 99.4%))",
      }}
    >
      {/* Whole band is the marquee. Two viewport-wide strips -> -50% loops seamlessly,
          duplicate always offscreen. ~45s, slow, right-to-left. Pauses on hover. */}
      <div className="flex w-max animate-vh-marquee" style={{ animationDuration: "26s" }}>
        <Strip />
        <Strip ariaHidden />
      </div>
    </section>
  );
}
